import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { FC, Fragment, useState } from "react";
import { PdfData } from "@/models/pdfData";
import { millisToTime } from "@/services/dateTimeService";
import { useResponsiveContext } from "@context/ResponsiveContext";

export interface GameSelectionDropdownOption extends PdfData {
  prettyDate: string;
}

const GameSelectionDropdown: FC<{
  loadingGames: boolean;
  gameOptions: GameSelectionDropdownOption[];
  onGameSelected: (samsId: string) => void;
  samsIdError: boolean;
}> = ({ loadingGames, gameOptions, onGameSelected, samsIdError }) => {
  const { onMobile } = useResponsiveContext();

  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const handleAutocompleteOpen = () => {
    setAutocompleteOpen(true);
  };

  const handleAutocompleteClose = () => {
    setAutocompleteOpen(false);
  };

  return (
    <Autocomplete
      id="samsId"
      //error={samsIdError}
      disabled={loadingGames}
      open={autocompleteOpen}
      groupBy={(option) => option.prettyDate.substring(0, 8)}
      onOpen={handleAutocompleteOpen}
      onClose={handleAutocompleteClose}
      onChange={(event, newValue) =>
        newValue && onGameSelected(newValue.samsId)
      }
      filterOptions={(options, state) => {
        return options.filter((o) =>
          [
            o.samsId,
            o.gameData.location,
            o.gameData.gameNumber,
            o.gameData.team1.longName,
            o.gameData.team1.shortName,
            o.gameData.team2.longName,
            o.gameData.team2.shortName,
            o.prettyDate,
          ].some(
            (v) =>
              v && v.toLowerCase().includes(state.inputValue.toLowerCase()),
          ),
        );
      }}
      isOptionEqualToValue={(option, value) => option.samsId === value.samsId}
      options={gameOptions}
      loading={loadingGames}
      renderOption={(props, option) => (
        <li {...props} style={{ padding: onMobile ? "10px 6px" : "16px 24px" }}>
          <Box
            display={"flex"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            gap={"20px"}
            maxWidth={"100%"}
          >
            {!onMobile && gameOptions.length < 200 && (
              <>
                <Avatar
                  src={option.gameData.team1.iconUrl}
                  slotProps={{ img: { loading: "lazy" } }}
                />
                <Avatar
                  src={option.gameData.team2.iconUrl}
                  slotProps={{ img: { loading: "lazy" } }}
                />
              </>
            )}
            <Typography
              whiteSpace={"nowrap"}
              overflow={"hidden"}
              textOverflow={"ellipsis"}
              pr={"40px"}
            >
              {`${millisToTime(option.gameData.gameStartDateTime)} Uhr: ${option.gameData.team1[onMobile ? "shortName" : "longName"]} vs. ${option.gameData.team2[onMobile ? "shortName" : "longName"]}`}
            </Typography>
          </Box>
        </li>
      )}
      getOptionLabel={(option) => option.samsId}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          required
          margin="dense"
          id="samsId"
          type="text"
          fullWidth
          label="Spiel auswählen"
          error={samsIdError}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loadingGames ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </Fragment>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default GameSelectionDropdown;
